/**
 * useTestimonials — paginated public testimonials feed from the NoJo proxy.
 *
 * Hits `/api/integrations/testimonials?lang=&page=&per_page=` and returns
 * a single page at a time (NOT an accumulating list). Each page change
 * replaces `items`, scrolls the caller to the top of the list, and emits
 * a fresh fetch.
 *
 * Owner directive (Feb 2026): "Instead of 'load more', create proper
 * pagination — first/prev/numbered/next/last."
 *
 * Returns:
 *   {
 *     items,            // current page only
 *     total,            // total across all pages
 *     page,             // current 0-indexed page
 *     totalPages,
 *     perPage,
 *     loading, error,
 *     goToPage(n),      // 0-indexed clamped page jump
 *   }
 */
import { useCallback, useEffect, useRef, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 30;

// localStorage cache — preserves the last successful response across brief
// upstream outages (e.g. while a deploy is rolling on the partner side).
// Visitors get to keep reading real reviews instead of seeing a hard error.
//
// CACHE KEY VERSION HISTORY:
//   v1 (implicit) → pre-2026-07-08 responses could have raw `Enc:` city
//     ciphertext or literal `Unavailable` display names cached client-side.
//   v2 → post-shield: sanitized responses only. Bumping the prefix
//     invalidates all v1 caches on the first visit so returning users
//     never see the pre-shield strings from their own browser cache.
const CACHE_KEY_PREFIX = "nojo:testimonials:v2:";
function cacheKey(lang, page, perPage) {
  return `${CACHE_KEY_PREFIX}${lang}:${page}:${perPage}`;
}
function readCache(lang, page, perPage) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(cacheKey(lang, page, perPage));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return null;
    return parsed;
  } catch {
    return null;
  }
}
function writeCache(lang, page, perPage, payload) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      cacheKey(lang, page, perPage),
      JSON.stringify({ ...payload, ts: Date.now() }),
    );
  } catch {
    // Quota / private mode — silently skip; runtime behaviour unaffected.
  }
}

export default function useTestimonials({
  lang = "en",
  initialPageSize = PAGE_SIZE,
  initialPage = 0,
} = {}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // `stale` = we are showing cached data because the upstream call failed
  // (e.g. partner deploy in progress). Surfaced to the UI so it can show a
  // soft "syncing" banner instead of the hard error state.
  const [stale, setStale] = useState(false);
  const [perPage] = useState(initialPageSize);
  // Authoritative per-service sync timestamps from the same payload —
  // powers the Testimonials hub's SourceFreshnessBadge. Reuses the exact
  // field the homepage/service-detail rotators already consume; no new
  // backend surface, no internal pipeline detail exposed.
  const [sourceFreshness, setSourceFreshness] = useState([]);
  const abortRef = useRef(null);

  const fetchPage = useCallback(
    async (pageIndex) => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);

      // Prime UI with cached data immediately so the visitor sees real
      // reviews while the network call is in flight. The cache is only
      // shown if there's nothing else on screen yet.
      const cached = readCache(lang, pageIndex, perPage);
      if (cached) {
        setItems(cached.items);
        setTotal(cached.total ?? cached.items.length);
        setPage(pageIndex);
        setSourceFreshness(Array.isArray(cached.sourceFreshness) ? cached.sourceFreshness : []);
      }

      const handleSoftFail = () => {
        const fallback = readCache(lang, pageIndex, perPage);
        if (fallback) {
          setItems(fallback.items);
          setTotal(fallback.total ?? fallback.items.length);
          setPage(pageIndex);
          setSourceFreshness(Array.isArray(fallback.sourceFreshness) ? fallback.sourceFreshness : []);
          setStale(true);
          setError(false);
        } else {
          setError(true);
          setStale(false);
        }
        setLoading(false);
      };

      try {
        const url = `${BACKEND}/api/integrations/testimonials?lang=${lang}&page=${pageIndex}&per_page=${perPage}`;
        const res = await fetch(url, { signal: ctrl.signal });
        const json = await res.json();
        if (!json.ok) {
          handleSoftFail();
          return;
        }
        const incoming = Array.isArray(json.items) ? json.items : [];
        setError(false);
        setStale(false);
        setTotal(json.total || incoming.length);
        setItems(incoming); // page-replace, not append
        setPage(pageIndex);
        setSourceFreshness(Array.isArray(json.sourceFreshness) ? json.sourceFreshness : []);
        setLoading(false);
        writeCache(lang, pageIndex, perPage, {
          items: incoming,
          total: json.total || incoming.length,
          sourceFreshness: Array.isArray(json.sourceFreshness) ? json.sourceFreshness : [],
        });
      } catch (e) {
        if (e?.name !== "AbortError") {
          handleSoftFail();
        }
      }
    },
    [lang, perPage]
  );

  // Initial load + language change → fetch the initialPage (URL-driven)
  useEffect(() => {
    setPage(initialPage);
    fetchPage(initialPage);
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchPage, initialPage]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const goToPage = useCallback(
    (n) => {
      if (loading) return;
      const clamped = Math.max(0, Math.min(n, totalPages - 1));
      if (clamped === page) return;
      fetchPage(clamped);
    },
    [fetchPage, loading, page, totalPages]
  );

  return {
    items,
    total,
    page,
    totalPages,
    perPage,
    loading,
    error,
    stale,
    sourceFreshness,
    goToPage,
  };
}
